import { type ConsultantDetail, createConsultantSchema } from '#shared/types';
import { Banner } from '#client/components/ui/Banner';
import { Card } from '#client/components/ui/Card';
import { Checkbox } from '#client/components/ui/Checkbox';
import { FormActions } from '#client/components/ui/FormActions';
import { ResourceFormShell } from '#client/components/ui/ResourceFormShell';
import { SignatureField } from '#client/components/ui/SignatureField';
import { TextField } from '#client/components/ui/TextField';
import { useFormValues } from '#client/hooks/useFormValues';
import { useResourceForm } from '#client/hooks/useResourceForm';
import { RESOURCE } from '#client/lib/routes';
import { initialConsultantValues } from './consultant-form.logic';
import { SIGNATURE_MAX_BYTES } from '#shared/types';

const PATH = RESOURCE.consultants;

export function ConsultantFormPage() {
  return (
    <ResourceFormShell<ConsultantDetail> path={PATH} noun="consultant">
      {({ id, record }) => <ConsultantForm id={id} record={record} />}
    </ResourceFormShell>
  );
}

function ConsultantForm({
  id,
  record,
}: {
  id?: string | undefined;
  record?: ConsultantDetail | undefined;
}) {
  const { values, set } = useFormValues(initialConsultantValues(record));

  const form = useResourceForm({
    path: PATH,
    id,
    schema: createConsultantSchema,
    noun: 'Consultant',
  });

  return (
    <form onSubmit={form.handleSubmit(values)} noValidate>
      <div className="c-form-stack">
        {form.formError && <Banner tone="critical">{form.formError}</Banner>}

        <Card title="Reporting doctor">
          <div className="c-form-grid">
            <TextField
              label="Name"
              value={values.name}
              error={form.errors.name}
              onChange={(event) => set('name', event.target.value)}
              // The PDF standard fonts cover Latin-1 only, so a name outside it
              // renders as mojibake on paper while looking correct here.
              helpText="As it should print. Latin characters only."
              required
            />
            <TextField
              label="Designation"
              value={values.designation}
              error={form.errors.designation}
              onChange={(event) => set('designation', event.target.value)}
              helpText="e.g. Consultant Pathologist"
              required
            />
            <TextField
              label="Qualifications"
              value={values.qualifications}
              error={form.errors.qualifications}
              onChange={(event) => set('qualifications', event.target.value)}
              helpText="One line, e.g. MBBS, FCPS (Haematology)"
              required
            />
            <TextField
              label="Registration number"
              value={values.registration_no ?? ''}
              error={form.errors.registration_no}
              onChange={(event) => set('registration_no', event.target.value)}
              helpText="PMDC / PMC number, if it should print"
            />
          </div>

          <Checkbox
            label="Active"
            checked={values.is_active}
            onChange={(event) => set('is_active', event.target.checked)}
          />
        </Card>

        <Card
          title="Signature"
          subtitle="Printed under this doctor's name on reports they sign off"
        >
          <SignatureField
            label="Signature image"
            value={values.signature_data_url ?? null}
            onChange={(dataUrl) => set('signature_data_url', dataUrl)}
            error={form.errors.signature_data_url}
            helpText={`PNG or JPEG under ${SIGNATURE_MAX_BYTES / 1024} KB. A transparent PNG scanned at about 60 × 20 mm prints best.`}
          />
        </Card>

        <FormActions
          cancelTo={PATH}
          submitLabel={form.isEdit ? 'Save changes' : 'Add consultant'}
          submitting={form.submitting}
        />
      </div>
    </form>
  );
}
